import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Collapse,
  IconButton,
  Divider,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress
} from '@mui/material';
import {
  Description as DocumentIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Send as SendIcon,
  History as HistoryIcon,
  Article as ArticleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  generateDocumentsDirect,
  callAiDraftingAgent,
  downloadDocument,
  getDocumentKey,
  fetchTemplateMetadata,
  fetchMultipleTemplateMetadata
} from '../services/documentService';
import { getCurrentUserInfo } from '../utils/auth';
import {
  getActiveSessionId,
  getCachedSessionId
} from '../utils/sessionManagerV2';
import {
  storeSessionHistory,
  getSessionHistory
} from '../utils/sessionManager';

// Progress tracking component for long-running operations
const GenerationProgress = ({ startTime, documentType, documentCount = 1, documentList = [] }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const isMultiDoc = documentCount > 1;
  // Estimate ~15 seconds per document, with minimum of 30 seconds
  const expectedTime = Math.max(30, documentCount * 15);
  const progress = Math.min((elapsed / expectedTime) * 100, 95); // Cap at 95% until complete

  // Build document list display text
  const docListText = documentList.length > 0
    ? documentList.join(', ')
    : documentType;

  return (
    <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1, color: 'primary.contrastText' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" fontWeight="medium">
          Generating {isMultiDoc ? `${documentCount} documents` : documentType}...
        </Typography>
        <Typography variant="body2">
          {elapsed}s / ~{expectedTime}s
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'primary.dark',
          '& .MuiLinearProgress-bar': {
            bgcolor: 'white',
            borderRadius: 4
          }
        }}
      />
      <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>
        {isMultiDoc
          ? `Creating: ${docListText}`
          : "Preparing your document..."
        }
      </Typography>
    </Box>
  );
};

// Helper function to extract articles from S3 metadata format
const extractArticlesFromMetadata = (metadata) => {
  if (!metadata || !metadata.structure) {
    console.log('No metadata or structure found');
    return null;
  }
  
  // The metadata structure varies by document type
  // For Will: metadata.structure.will.articles
  // For Trust: metadata.structure.trust.articles
  // etc.
  
  // Find the first key in structure that has articles
  const structureKeys = Object.keys(metadata.structure);
  for (const key of structureKeys) {
    if (metadata.structure[key] && metadata.structure[key].articles) {
      console.log(`Found articles under structure.${key}`);
      return {
        articles: metadata.structure[key].articles.map(article => ({
          id: article.id,
          title: article.title,
          customizable: article.customizable !== false,
          purpose: article.purpose || 'administrative'
        }))
      };
    }
  }
  
  console.log('No articles found in metadata structure');
  return null;
};

// Helper function to get sections for a document
const getSectionsForDocument = (doc) => {
  if (doc.metadata && doc.metadata.articles) {
    // Map metadata articles to our section format
    return doc.metadata.articles.map((article, index) => {
      // Extract article number from the id (e.g., "article-i" -> "Article I")
      // or generate it based on index
      let articleNumber = '';
      if (article.id && article.id.startsWith('article-')) {
        // Convert article-i to Article I, article-ii to Article II, etc.
        const romanMap = {
          'i': 'I', 'ii': 'II', 'iii': 'III', 'iv': 'IV', 'v': 'V',
          'vi': 'VI', 'vii': 'VII', 'viii': 'VIII', 'ix': 'IX', 'x': 'X',
          'xi': 'XI', 'xii': 'XII', 'xiii': 'XIII', 'xiv': 'XIV', 'xv': 'XV',
          'xvi': 'XVI', 'xvii': 'XVII', 'xviii': 'XVIII', 'xix': 'XIX', 'xx': 'XX',
          'xxi': 'XXI', 'xxii': 'XXII', 'xxiii': 'XXIII', 'xxiv': 'XXIV', 'xxv': 'XXV',
          'xxvi': 'XXVI', 'xxvii': 'XXVII', 'xxviii': 'XXVIII', 'xxix': 'XXIX', 'xxx': 'XXX',
          'xxxi': 'XXXI', 'xxxii': 'XXXII', 'xxxiii': 'XXXIII', 'xxxiv': 'XXXIV'
        };
        const numeral = article.id.replace('article-', '');
        articleNumber = `Article ${romanMap[numeral] || numeral.toUpperCase()}`;
      } else {
        // Fallback: generate based on index
        const romans = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 
                       'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX',
                       'XXI', 'XXII', 'XXIII', 'XXIV', 'XXV', 'XXVI', 'XXVII', 'XXVIII', 'XXIX', 'XXX',
                       'XXXI', 'XXXII', 'XXXIII', 'XXXIV', 'XXXV'];
        articleNumber = `Article ${romans[index] || (index + 1)}`;
      }
      
      return {
        id: article.id, // Use the id directly from metadata
        label: `${articleNumber} - ${article.title}`, // Include article number
        customizable: article.customizable !== false,
        purpose: article.purpose
      };
    });
  }
  // Default sections for documents without metadata
  return [
    { id: 'article-i', label: 'Article I - Introduction' },
    { id: 'article-ii', label: 'Article II - Main Provisions' },
    { id: 'article-iii', label: 'Article III - Additional Terms' },
    { id: 'article-iv', label: 'Article IV - Administrative Provisions' },
    { id: 'article-v', label: 'Article V - Signatures' }
  ];
};

// Document Outline Tree Component
const DocumentOutlineTree = ({ documents, onSelectSection, selectedDocumentId, onSelectDocument, selectedSection }) => {
  const [expandedDocs, setExpandedDocs] = useState({});

  const handleToggleDoc = (docId) => {
    setExpandedDocs(prev => ({
      ...prev,
      [docId]: !prev[docId]
    }));
  };


  return (
    <Paper sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Current Document Outline
          </Typography>
          <Tooltip title="Click on articles to direct AI edits to specific sections">
            <InfoIcon fontSize="small" color="action" />
          </Tooltip>
        </Box>
        {documents.length > 0 && selectedDocumentId && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DocumentIcon fontSize="small" color="primary" />
            <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
              {documents.find(doc => doc.id === selectedDocumentId)?.name || 'Select a document'}
            </Typography>
            {documents.find(doc => doc.id === selectedDocumentId)?.isEdited && (
              <Chip label="Edited" size="small" color="success" sx={{ height: 16 }} />
            )}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Select documents and click articles to focus edits
        </Typography>
      </Box>
      {/* Scrollable document list */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <List dense>
        {documents.map((doc) => (
          <Box key={doc.id}>
            <ListItem
              button
              onClick={() => {
                handleToggleDoc(doc.id);
                if (onSelectDocument) onSelectDocument(doc.id);
              }}
              sx={{ 
                pl: 1,
                bgcolor: selectedDocumentId === doc.id ? 'action.selected' : 'inherit'
              }}
            >
              <ListItemIcon>
                {expandedDocs[doc.id] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </ListItemIcon>
              <ListItemIcon>
                <DocumentIcon fontSize="small" color={doc.isEdited ? "success" : "inherit"} />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {doc.name}
                    {doc.isEdited && (
                      <Chip label="Edited" size="small" color="success" sx={{ height: 18 }} />
                    )}
                  </Box>
                }
              />
            </ListItem>
            <Collapse in={expandedDocs[doc.id]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {getSectionsForDocument(doc).map((section) => (
                  <ListItem
                    key={section.id}
                    button
                    sx={{ 
                      pl: 6,
                      '&:hover': {
                        bgcolor: 'action.hover',
                        cursor: 'pointer'
                      },
                      bgcolor: selectedSection === `${doc.id}-${section.id}` ? 'action.selected' : 'inherit'
                    }}
                    onClick={() => onSelectSection(`${doc.id}-${section.id}`)}
                  >
                    <ListItemIcon>
                      <ArticleIcon fontSize="small" color={selectedSection === `${doc.id}-${section.id}` ? 'primary' : 'inherit'} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: selectedSection === `${doc.id}-${section.id}` ? 'bold' : 'normal' }}
                        >
                          {section.label}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Box>
        ))}
        </List>
      </Box>
    </Paper>
  );
};


// Change Log Component
const ChangeLog = ({ changes }) => {
  return (
    <Box sx={{ p: 2 }}>
      {changes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          No changes yet
        </Typography>
      ) : (
        <List dense sx={{ pt: 0 }}>
          {changes.map((change, index) => {
            const isSummary = change.description.startsWith('✅');
            return (
              <ListItem 
                key={index} 
                sx={{ 
                  px: 0,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  borderBottom: isSummary ? '2px solid #e0e0e0' : 'none',
                  pb: isSummary ? 2 : 1,
                  mb: isSummary ? 2 : 0
                }}
              >
                <ListItemText
                  primary={
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: isSummary ? '0.9rem' : '0.85rem',
                        fontWeight: isSummary ? 'bold' : 'normal',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxWidth: '100%'
                      }}
                    >
                      {change.description}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {new Date(change.timestamp).toLocaleTimeString()}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};

// Chat Message Component
const ChatMessage = ({ message }) => {
  const isUser = message.role === 'user';
  
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2,
        px: 2
      }}
    >
      <Box
        sx={{
          maxWidth: '75%',
          minWidth: '150px',
          p: 2,
          borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
          bgcolor: isUser ? '#d4f1d4' : '#e3f2fd', // Light green for user, light blue for agent
          color: 'text.primary',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          position: 'relative'
        }}
      >
        {!isUser && (
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'block', mb: 0.5 }}>
            AI Assistant
          </Typography>
        )}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          {message.content}
        </Typography>
        {message.timestamp && (
          <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mt: 0.5, textAlign: isUser ? 'right' : 'left' }}>
            {new Date(message.timestamp).toLocaleTimeString()}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const AIDrafting = ({ 
  selectedTemplates, 
  clientData,
  onDraftingComplete,
  onBack,
  existingSessionId = null,
  existingDocuments = []
}) => {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [changes, setChanges] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [generationStatus, setGenerationStatus] = useState('idle'); // idle, generating, completed, error
  const [generationProgress, setGenerationProgress] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    // Only auto-scroll for user messages or short AI messages
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && (lastMessage.role === 'user' || lastMessage.content.length < 200)) {
      scrollToBottom();
    }
  }, [messages]);

  // Initialize user info and session on mount
  useEffect(() => {
    const initSession = async () => {
      // Get user info
      const user = await getCurrentUserInfo();
      setUserInfo(user);

      // Check if we have an existing session (back to editing flow)
      if (existingSessionId) {
        console.log('[AIDrafting] Resuming existing session:', existingSessionId);
        setSessionId(existingSessionId);
        setDocuments(existingDocuments);
        setGenerationStatus('completed');

        // Set the first document as selected
        if (existingDocuments.length > 0) {
          setSelectedDocumentId(existingDocuments[0].id);
        }

        // Load existing history from localStorage
        const existingHistory = getSessionHistory(existingSessionId);
        if (existingHistory && existingHistory.length > 0) {
          setChanges(existingHistory);
          console.log('[AIDrafting] Loaded', existingHistory.length, 'history items');
        }

        // Add message for returning to editing
        addMessage('assistant',
          `Welcome back! I've loaded your existing documents. What would you like to edit?`
        );
      } else {
        // Fresh start - get new session ID from server
        console.log('[AIDrafting] Starting fresh session');
        const activeSessionId = await getActiveSessionId();
        setSessionId(activeSessionId);
        console.log('[AIDrafting] New session ID:', activeSessionId);

        // Ensure we start with clean history for new session
        setChanges([]);

        // Start document generation for new session
        // Pass sessionId directly to avoid React state race condition
        generateDocuments(activeSessionId);
      }
    };

    initSession();
  }, []);

  // Default template paths for common document types (used when paths not provided by templates)
  const getDefaultTemplatePath = (docName) => {
    const defaultPaths = {
      'Will': 'wills/will-pourover.docx',
      'Trust': 'trusts/trust-disclaimer.docx',
      'Financial Power of Attorney': 'powers/power-of-attorney-financial.docx',
      'Healthcare Power of Attorney': 'powers/power-of-attorney-healthcare.docx',
      'HIPAA Authorization': 'authorizations/HIPAA.docx'
    };
    return defaultPaths[docName] || '';
  };

  const generateDocuments = async (passedSessionId = null) => {
    setGenerationStatus('generating');
    setIsLoading(true);

    // Build complete list of documents to generate from all selections
    // This handles mixed selections of individual documents AND packages
    const allDocuments = [];  // Array of document names
    const allTemplatePaths = {};  // Map of document name -> template path

    selectedTemplates.forEach(template => {
      if (template.type === 'package') {
        // For packages, add all documents within the package
        // Package templates have a 'documents' array with document names
        if (template.documents && Array.isArray(template.documents)) {
          template.documents.forEach(docName => {
            if (!allDocuments.includes(docName)) {
              allDocuments.push(docName);
              // Use default path for package documents
              const defaultPath = getDefaultTemplatePath(docName);
              if (defaultPath) {
                allTemplatePaths[docName] = defaultPath;
              }
            }
          });
        }
      } else {
        // For individual documents, add directly
        if (!allDocuments.includes(template.name)) {
          allDocuments.push(template.name);
          if (template.path) {
            allTemplatePaths[template.name] = template.path;
          } else {
            // Fall back to default path
            const defaultPath = getDefaultTemplatePath(template.name);
            if (defaultPath) {
              allTemplatePaths[template.name] = defaultPath;
            }
          }
        }
      }
    });

    // Determine if we're generating multiple documents
    const isPackage = allDocuments.length > 1;

    // Determine the actual package name if a package template was selected
    const packageTemplate = selectedTemplates.find(t => t.type === 'package');

    // For API routing, use the actual package name (e.g., "Disclaimer Package")
    // so documentService.js can route to Lambda Function URL for long-running packages
    const documentType = isPackage && packageTemplate
      ? packageTemplate.name  // e.g., "Disclaimer Package", "Fractional QTIP Package"
      : (isPackage ? `Custom Package (${allDocuments.length} documents)` : allDocuments[0]);

    // Debug logging
    console.log('🔍 Generating documents:', allDocuments);
    console.log('📋 Template paths:', allTemplatePaths);
    console.log('📋 Selected templates:', selectedTemplates.map(t => ({ name: t.name, type: t.type, documents: t.documents })));

    setGenerationProgress({ startTime: Date.now(), documentType, documentCount: allDocuments.length, documentList: allDocuments });

    try {
      // Add initial AI message with actual document list
      const docListText = allDocuments.join(', ');
      addMessage('assistant', `I'm generating ${allDocuments.length} document${allDocuments.length > 1 ? 's' : ''}: ${docListText}. This will take a moment...`);

      // Call generateDocumentsDirect with all placeholder values
      // Use passedSessionId (passed as parameter) to avoid React state race condition
      const sessionToUse = passedSessionId || sessionId;

      const response = await generateDocumentsDirect({
        documentType: documentType,  // Uses actual package name for proper Lambda Function URL routing
        variables: clientData,
        userMessage: `Generate documents: ${docListText}`,
        isPackage,
        packageDocs: allDocuments,
        packageTemplatePaths: allTemplatePaths,
        templatePath: !isPackage && allTemplatePaths[allDocuments[0]] ? allTemplatePaths[allDocuments[0]] : '',
        placeholders: Object.keys(clientData),
        sessionId: sessionToUse,
        userSub: userInfo?.sub,
        firmId: userInfo?.firmId || 'default'
      });
      
      if (response.success) {
        const generatedDocs = [];
        
        if (response.documentUrls || response.packageUrls) {
          const urls = response.documentUrls || response.packageUrls;
          Object.entries(urls).forEach(([docType, url]) => {
            const docKey = getDocumentKey(url);
            generatedDocs.push({
              id: docKey,
              name: docType,
              url: url,
              key: docKey,
              content: null,
              metadata: null, // Will be fetched dynamically
              templatePath: null // Will be determined from response
            });
          });
        } else if (response.documentUrl) {
          const docKey = getDocumentKey(response.documentUrl);
          generatedDocs.push({
            id: docKey,
            name: documentType,
            url: response.documentUrl,
            key: docKey,
            content: null,
            metadata: null, // Will be fetched dynamically
            templatePath: null // Will be determined from response
          });
        }
        
        // Now fetch metadata for each document
        const templatePaths = [];

        // First, try to use the actual selected templates
        // For single documents, use the selected template directly
        if (!isPackage && selectedTemplates.length === 1) {
          generatedDocs.forEach(doc => {
            doc.templatePath = selectedTemplates[0].path;
            templatePaths.push(selectedTemplates[0].path);
          });
        } else {
          // For packages, we need to determine template paths based on what was actually selected
          // Build a dynamic mapping based on the selected package template
          const packageTemplate = selectedTemplates.find(t => t.type === 'package');

          // Use the package name to determine the correct trust template
          let trustTemplatePath = 'trusts/trust-disclaimer.docx'; // Default fallback

          if (packageTemplate) {
            const packageName = packageTemplate.name.toLowerCase();
            console.log('🔍 Determining trust template for package:', packageName);

            // Map package names to their correct trust templates
            if (packageName.includes('fractional') || packageName.includes('qtip')) {
              trustTemplatePath = 'trusts/trust-fractional-qtip.docx';
              console.log('✅ Using Fractional QTIP trust template');
            } else if (packageName.includes('probate') || packageName.includes('avoidance')) {
              trustTemplatePath = 'trusts/trust-probate-avoidance.docx';
              console.log('✅ Using Probate Avoidance trust template');
            } else if (packageName.includes('disclaimer')) {
              trustTemplatePath = 'trusts/trust-disclaimer.docx';
              console.log('✅ Using Disclaimer trust template');
            } else {
              console.warn('⚠️ Unknown package type, using default disclaimer template');
            }
          }

          // Dynamic template path mapping based on package selection
          const templatePathMap = {
            'Will': 'wills/will-pourover.docx',
            'Pour-Over Will': 'wills/will-pourover.docx',
            'Trust': trustTemplatePath, // ← Now dynamic based on package!
            'Disclaimer Trust': trustTemplatePath,
            'Revocable Trust': trustTemplatePath,
            'Fractional QTIP Trust': trustTemplatePath,
            'Probate Avoidance Trust': trustTemplatePath,
            'Financial Power of Attorney': 'powers/power-of-attorney-financial.docx',
            'Healthcare Power of Attorney': 'powers/power-of-attorney-healthcare.docx',
            'HIPAA Authorization': 'authorizations/HIPAA.docx',
            'Advance Directive': 'healthcare/advance-directive.docx',
            'Operating Agreement': 'business/operating-agreement.docx'
          };

          // Map document names to template paths
          generatedDocs.forEach(doc => {
            const templatePath = templatePathMap[doc.name] || selectedTemplates.find(t => t.name === doc.name)?.path;
            if (templatePath) {
              doc.templatePath = templatePath;
              templatePaths.push(templatePath);
              console.log(`📄 Mapped ${doc.name} to template: ${templatePath}`);
            } else {
              console.warn(`⚠️ No template path found for document: ${doc.name}`);
            }
          });
        }
        
        // Fetch metadata for all templates
        if (templatePaths.length > 0) {
          console.log('Fetching metadata for templates:', templatePaths);
          const metadataMap = await fetchMultipleTemplateMetadata(templatePaths);
          
          // Apply metadata to documents
          generatedDocs.forEach(doc => {
            if (doc.templatePath && metadataMap[doc.templatePath]) {
              console.log(`Applying metadata to ${doc.name} from ${doc.templatePath}`);
              doc.metadata = extractArticlesFromMetadata(metadataMap[doc.templatePath]);
            } else {
              console.warn(`No metadata found for ${doc.name}`);
            }
          });
        }
        
        setDocuments(generatedDocs);
        setGenerationStatus('completed');
        
        // Set the first document as selected by default
        if (generatedDocs.length > 0) {
          setSelectedDocumentId(generatedDocs[0].id);
        }
        
        addMessage('assistant', 
          `✅ Your documents have been generated successfully!\n\n` +
          `What would you like me to help you customize?`
        );
        
        // Add initial change log entry
        addChange('Documents generated from templates', null);
        
      } else {
        setGenerationStatus('error');
        addMessage('assistant', 
          `❌ There was an error generating your documents: ${response.error}\n\n` +
          `Please try again or contact support if the issue persists.`
        );
      }
    } catch (error) {
      console.error('Error generating documents:', error);
      setGenerationStatus('error');
      addMessage('assistant', 
        `❌ An unexpected error occurred while generating your documents.\n\n` +
        `Please try again or contact support.`
      );
    } finally {
      setIsLoading(false);
      setGenerationProgress(null); // Clear progress timer
    }
  };

  const addMessage = (role, content) => {
    const newMessage = {
      role,
      content,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const addChange = (description, section) => {
    const newChange = {
      description,
      section,
      timestamp: new Date().toISOString()
    };
    setChanges(prev => {
      const updatedChanges = [newChange, ...prev];
      // Save to localStorage whenever changes are updated
      if (sessionId) {
        storeSessionHistory(sessionId, updatedChanges);
      }
      return updatedChanges;
    });
  };

  const handleSendMessage = async () => {
    if (!userInput.trim() || isLoading) return;
    
    const message = userInput.trim();
    setUserInput('');
    addMessage('user', message);
    setIsLoading(true);
    
    try {
      // Use the selected document
      const currentDoc = documents.find(doc => doc.id === selectedDocumentId) || documents[0];
      if (!currentDoc) {
        addMessage('assistant', 'Please wait for the documents to finish generating before making edits.');
        setIsLoading(false);
        return;
      }
      
      // Add section context if one is selected
      let enhancedPrompt = message;
      let currentArticleLabel = null;
      
      if (selectedSection && selectedSection.startsWith(currentDoc.id)) {
        const sectionName = selectedSection.replace(`${currentDoc.id}-`, '');
        // Find the actual section label from the document's sections
        const currentDocSections = getSectionsForDocument(currentDoc);
        const matchedSection = currentDocSections.find(s => s.id === sectionName);
        currentArticleLabel = matchedSection ? matchedSection.label : sectionName;
        
        enhancedPrompt = `In ${currentArticleLabel}: ${message}`;
      }
      
      // Add immediate acknowledgment - brief and concise
      addMessage('assistant', `Working on your request...`);
      
      // Call AI drafting agent
      const response = await callAiDraftingAgent({
        docKey: currentDoc.key,
        prompt: enhancedPrompt,
        sessionId: sessionId,
        docType: currentDoc.name,
        userSub: userInfo?.sub,
        firmId: userInfo?.firmId || 'default'
      });
      
      if (response.needMoreInfo) {
        addMessage('assistant', response.needMoreInfoReason || 'OK, tell me more specifics about what you want to change.');
      } else if (response.success || response.instructions || response.processedDocumentKey || response.editedDocumentKey) {
        // Get the edited document key from various possible fields
        const editedKey = response.processedDocumentKey || response.editedDocumentKey || response.docKey;
        
        if (editedKey && editedKey !== currentDoc.key) {
          // Document was edited successfully
          const updatedDoc = {
            ...currentDoc,
            key: editedKey,
            url: downloadDocument(editedKey),
            isEdited: true,
            originalKey: currentDoc.originalKey || currentDoc.key,
            metadata: currentDoc.metadata // Preserve metadata for outline
          };
          
          setDocuments(prev => prev.map(doc => 
            doc.id === currentDoc.id ? updatedDoc : doc
          ));
          
          // Create a concise success message for the chat
          const changeCount = response.instructions ? response.instructions.length : 1;
          const successMessage = `✅ I've successfully updated your ${currentDoc.name} with ${changeCount} change${changeCount > 1 ? 's' : ''}. Check the History panel for details.`;
          
          addMessage('assistant', successMessage);
          
          // Log detailed changes to History panel
          if (response.instructions && response.instructions.length > 0) {
            // Add a summary entry with timestamp
            const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            addChange(`✅ ${timestamp} - Completed ${changeCount} edit${changeCount > 1 ? 's' : ''}`, null);
            
            // Then add individual changes showing what was actually added to the document
            response.instructions.forEach((inst, index) => {
              let changeDescription = '';
              
              // Show what was actually added/changed in the document
              if (inst.content) {
                // This is the actual content added
                changeDescription = `Added: "${inst.content}"`;
              } else if (inst.description && inst.description !== 'Document updated') {
                changeDescription = inst.description;
              } else if (inst.action) {
                changeDescription = `Action: ${inst.action}`;
              } else {
                changeDescription = `Change ${index + 1}`;
              }
              
              // Add article information if available
              let articleInfo = currentArticleLabel;
              
              // If no current article label, try to construct it from instruction data
              if (!articleInfo && inst.articleId) {
                // Find the article in the current document's metadata
                const currentDoc = documents.find(doc => doc.id === selectedDocumentId) || documents[0];
                if (currentDoc) {
                  const docSections = getSectionsForDocument(currentDoc);
                  const matchedSection = docSections.find(s => s.id === inst.articleId);
                  if (matchedSection) {
                    articleInfo = matchedSection.label;
                  } else {
                    // Fallback: format article-ii to Article II
                    const romanMap = {
                      'i': 'I', 'ii': 'II', 'iii': 'III', 'iv': 'IV', 'v': 'V',
                      'vi': 'VI', 'vii': 'VII', 'viii': 'VIII', 'ix': 'IX', 'x': 'X',
                      'xi': 'XI', 'xii': 'XII', 'xiii': 'XIII', 'xiv': 'XIV', 'xv': 'XV',
                      'xvi': 'XVI'
                    };
                    const match = inst.articleId.match(/article-([a-z]+)/);
                    if (match) {
                      articleInfo = `Article ${romanMap[match[1]] || match[1].toUpperCase()}`;
                    }
                  }
                }
              }
              
              if (articleInfo) {
                // Format as "Article II - Tangible Personal Property" on the same line
                changeDescription += ` in ${articleInfo}`;
              }
              
              addChange(changeDescription, inst.section);
            });
          } else {
            let changeText = `✅ Completed edit: "${message}"`;
            if (currentArticleLabel) {
              changeText += ` in ${currentArticleLabel}`;
            }
            addChange(changeText, null);
          }
        } else {
          // No document key change but still might be success
          addMessage('assistant', 'I\'ve processed your request. The document has been updated.');
        }
      } else if (response.message || response.assistantMessage) {
        // Check if the message is asking for more info
        const responseMsg = response.message || response.assistantMessage;
        if (responseMsg.toLowerCase().includes('please') || responseMsg.toLowerCase().includes('could you')) {
          addMessage('assistant', `OK, ${responseMsg}`);
        } else {
          addMessage('assistant', responseMsg);
        }
      } else {
        addMessage('assistant', 'I was unable to process your request. Could you please rephrase it or provide more details?');
      }
      
    } catch (error) {
      console.error('Error processing AI drafting request:', error);
      addMessage('assistant', 'Sorry, I encountered an error processing your request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  const handleSelectSection = (sectionId) => {
    setSelectedSection(sectionId);
    
    // Extract document and article info
    const [docId, ...sectionParts] = sectionId.split('-');
    const sectionName = sectionParts.join('-');
    const doc = documents.find(d => d.id === docId);
    
    if (doc && sectionName) {
      // Find the article label from the document's actual sections
      const docSections = getSectionsForDocument(doc);
      const matchedSection = docSections.find(s => s.id === sectionName);
      const articleLabel = matchedSection ? matchedSection.label : sectionName;
      
      // Add a brief helpful message to the chat
      addMessage('assistant', 
        `📍 Selected: ${articleLabel}`
      );
    }
  };

  const handleContinue = () => {
    if (documents.length > 0) {
      // Pass both documents and session ID back to parent
      onDraftingComplete(documents, sessionId);
    }
  };

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'background.default'
    }}>
      {/* Fixed Header */}
      <Box sx={{ 
        flexShrink: 0, 
        p: 2,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <Typography variant="h4">
          AI Document Drafting
        </Typography>
      </Box>
      
      {/* Main Content Area - Fixed height, internal scrolling only */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        minHeight: 0,
        width: '100%'
      }}>
        {/* Left Panel - Document Outline - Fixed width */}
        <Box sx={{ 
          width: '25%', // Fixed 25% width
          minWidth: 250, // Minimum width
          maxWidth: 350, // Maximum width
          height: '100%', 
          borderRight: 1,
          borderColor: 'divider',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0
        }}>
          <DocumentOutlineTree 
            documents={documents} 
            onSelectSection={handleSelectSection}
            selectedDocumentId={selectedDocumentId}
            onSelectDocument={setSelectedDocumentId}
            selectedSection={selectedSection}
          />
        </Box>
        
        {/* Center Panel - Chat Interface - Flexible width */}
        <Box sx={{ 
          flex: 1, // Take remaining space
          height: '100%', 
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 400 // Minimum width for usability
        }}>
          <Paper sx={{ 
            height: '100%',
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* AI Assistant Header - Always shown */}
            <Box sx={{ 
              p: 2, 
              bgcolor: 'primary.light', 
              color: 'primary.contrastText',
              borderBottom: 2,
              borderColor: 'primary.main',
              flexShrink: 0
            }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                AI Assistant
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                I'm here to help customize your estate planning documents
              </Typography>
            </Box>
            
            {/* Chat Messages - This is the main scrollable area */}
            <Box sx={{ 
              flex: '1 1 auto', // This is key - allow it to grow and shrink
              overflow: 'auto',
              bgcolor: '#fafafa',
              p: 2,
              // Custom scrollbar styling
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#555',
              }
            }}>
              {generationStatus === 'generating' && generationProgress && (
                <Box sx={{ p: 4 }}>
                  <GenerationProgress
                    startTime={generationProgress.startTime}
                    documentType={generationProgress.documentType}
                    documentCount={generationProgress.documentCount}
                    documentList={generationProgress.documentList}
                  />
                </Box>
              )}
              {messages.length === 0 && generationStatus === 'idle' && (
                <Box sx={{ textAlign: 'center', color: 'text.secondary', p: 4 }}>
                  <Typography>Generating your documents...</Typography>
                </Box>
              )}
              {messages.map((msg, index) => (
                <ChatMessage key={index} message={msg} />
              ))}
              <div ref={messagesEndRef} />
            </Box>
            
            {/* Input Area - Fixed at bottom */}
            <Box sx={{ 
              flexShrink: 0, 
              borderTop: 1, 
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}>
              {/* Document selector for multiple documents */}
              {documents.length > 1 && (
                <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Document to Edit</InputLabel>
                    <Select
                      value={selectedDocumentId || ''}
                      onChange={(e) => setSelectedDocumentId(e.target.value)}
                      label="Select Document to Edit"
                    >
                      {documents.map((doc) => (
                        <MenuItem key={doc.id} value={doc.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DocumentIcon fontSize="small" />
                            <span>{doc.name}</span>
                            {doc.isEdited && (
                              <Chip label="Edited" size="small" color="success" />
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              
              {/* Text Input */}
              <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Type your editing request..."
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={isLoading || generationStatus !== 'completed'}
                  multiline
                  maxRows={3}
                  size="small"
                />
                <IconButton 
                  onClick={handleSendMessage}
                  disabled={isLoading || !userInput.trim() || generationStatus !== 'completed'}
                  color="primary"
                  sx={{ 
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&:disabled': { bgcolor: 'action.disabledBackground' },
                    flexShrink: 0,
                    alignSelf: 'flex-end'
                  }}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        </Box>
        
        {/* Right Panel - History - Fixed width */}
        <Box sx={{ 
          width: '20%', // Fixed 20% width
          minWidth: 200, // Minimum width
          maxWidth: 300, // Maximum width
          height: '100%', 
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0
        }}>
          <Paper sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon fontSize="small" />
                History
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              <ChangeLog changes={changes} />
            </Box>
          </Paper>
        </Box>
      </Box>
      
      {/* Fixed Footer */}
      <Box sx={{ 
        flexShrink: 0,
        p: 2,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <Button onClick={onBack} size="large">
          Back
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleContinue}
          disabled={documents.length === 0 || generationStatus === 'generating'}
          endIcon={<CheckIcon />}
        >
          Continue to Download
        </Button>
      </Box>
    </Box>
  );
};

export default AIDrafting;